import { useEffect, useState } from 'react';
import { Button, Icon } from '@iwoca/orion';
import {
    FormattedMessage,
    WrappedComponentProps,
    injectIntl
} from 'react-intl';
import { Dispatch } from 'redux';

import { MainSectionWrapper } from '../../pageStructure/mainSectionWrapper';
import messages from './DeferredView.messages';
import styles from './DeferredView.module.css';
import { connect } from 'react-redux';
import { DEFERRED_APPLICATION_REAPPLY } from '../MyOffers.actions';
import { RootState } from '../../../iwProvider/configureStore';
import { AMPLITUDE_EVENT_NAMES } from '../../../tracking/amplitudeEvents';
import { useQuery } from '@tanstack/react-query';
import { fetchGetApplication } from '@iwoca/lapi-client/edge';
import MainContentPending from '../../pageStructure/mainContentPending';
import FallbackErrorModal from '../../modals/fallbackErrorModal/FallbackErrorModal';
import { RequestedProduct } from '../MyOffers.types';
import { getCountry } from '@iwIntlProvider/CountryProvider';
import { AMPLITUDE_CATEGORY } from '../../../tracking/amplitudeCategories';
import { tracking } from '@iwoca/frontend-tracking-library';

const mapDispatchToProps = (dispatch: Dispatch) => ({
    reapply: (requestedProduct: RequestedProduct) =>
        dispatch({
            type: DEFERRED_APPLICATION_REAPPLY,
            currentRequestedProduct: requestedProduct
        })
});

const mapStateToProps = ({ siteContext }: RootState) => ({
    stateKey: siteContext.impersonatingStateKey || siteContext.state_key
});

type InjectedProps = WrappedComponentProps &
    ReturnType<typeof mapDispatchToProps> &
    ReturnType<typeof mapStateToProps>;

const DeferredView = ({ intl, reapply, stateKey }: InjectedProps) => {
    const [submittingNewApplication, setSubmittingNewApplication] =
        useState(false);
    const {
        isLoading,
        isError,
        data: getApplicationResponse
    } = useQuery({
        queryKey: ['application', stateKey],
        queryFn: () => fetchGetApplication({ stateKey: stateKey! })
    });

    useEffect(() => {
        tracking.ampli.track({
            event_type: AMPLITUDE_EVENT_NAMES.APPLICATION_ON_HOLD_VIEWED,
            event_properties: {
                category:
                    getCountry() === 'DE'
                        ? AMPLITUDE_CATEGORY.ACCOUNT_PAGES_DE
                        : AMPLITUDE_CATEGORY.ACCOUNT_PAGES
            }
        });
    }, []);

    function reApplyForLoan(requestedProduct: RequestedProduct) {
        setSubmittingNewApplication(true);
        tracking.ampli.track({
            event_type: AMPLITUDE_EVENT_NAMES.APPLICATION_ON_HOLD_CTA_CLICKED,
            event_properties: {
                category:
                    getCountry() === 'DE'
                        ? AMPLITUDE_CATEGORY.ACCOUNT_PAGES_DE
                        : AMPLITUDE_CATEGORY.ACCOUNT_PAGES
            }
        });

        reapply(requestedProduct);
    }
    if (isLoading) {
        return <MainContentPending data-testid="deferred-view-loading" />;
    }
    if (isError) {
        return <FallbackErrorModal />;
    }

    const requestedProduct: RequestedProduct =
        getApplicationResponse.data!.requested_products![0];

    return (
        <MainSectionWrapper>
            <div className={styles.innerWrapper}>
                <div className={styles.container}>
                    <div className={styles.fileSearchIconContainer}>
                        <Icon
                            className={styles.fileSearchIcon}
                            icon="fileVisualizationFilled"
                        />
                    </div>
                    <div className={styles.title}>
                        <FormattedMessage {...messages.title} />
                    </div>
                    <div className={styles.subtitle} data-testid="subtitle">
                        <FormattedMessage {...messages.subtitle} />
                    </div>
                    <Button
                        size="large"
                        variant="primary"
                        className={styles.reApplyCTA}
                        data-testid="reapply-cta"
                        loading={submittingNewApplication}
                        onClick={() => reApplyForLoan(requestedProduct)}
                    >
                        <FormattedMessage {...messages.reApplyButtonText} />
                    </Button>
                    <div
                        className={styles.underButtonText}
                        data-testid="underButtonText"
                    >
                        <FormattedMessage
                            {...messages.underButtonText}
                            values={{
                                link: (
                                    <a
                                        className={styles.phoneLink}
                                        href={`tel:${intl.formatMessage(
                                            messages.callPhoneNumber
                                        )}`}
                                    >
                                        <FormattedMessage
                                            {...messages.displayPhoneNumber}
                                        />
                                    </a>
                                )
                            }}
                        />
                    </div>
                </div>
            </div>
        </MainSectionWrapper>
    );
};

// eslint-disable-next-line import/no-default-export
export default connect(
    mapStateToProps,
    mapDispatchToProps
)(injectIntl(DeferredView));
